import React from 'react';
import {FieldHookConfig} from 'formik';
import {DatePicker, DatePickerProps} from '@material-ui/pickers';
import {useField, useFormikContext} from 'formik';
import formatISODate from 'date-fns/fp/formatISOWithOptions';

function hasTimeComponent(date: Readonly<string>): boolean {
  return date.length > 10;
}

/**
 * Internally the DatePicker component stores local date values
 * with time components, the values of which unfortunately matter.
 * This implementation has several quirks specific to working around that problem,
 * and it's not designed for general use.
 **/
 function isoDateWithLocalTimeAndZone(date: string|undefined): string|null {
  if (!date) {
    // Best practice is to use "undefined", but "null" is what
    // keeps the DatePicker component happy.
    return null;
  }
  else if (hasTimeComponent(date)) {
    return date;
  }
  else {
    // DatePicker expects the complete ISO representation with a 0000 hours UTC time component,
    // but it doesn't want the actual representation of 0000 hours on the day in question.
    // What it really wants is the ISO date, spliced together with a time component
    // representing 0000 hours UTC.
    const midnight = formatISODate({representation: 'time'}, Date.UTC(0, 0));
    return `${date}T${midnight}`;
  }
}

const FormikDatePicker: React.FC<Omit<DatePickerProps, 'onChange'> & FieldHookConfig<string|undefined>> = (props) => {
  const {setFieldValue} = useFormikContext();
  const [field] = useField(props);
  // FormikDatePicker supports displaying ISO date values in the form YYYY-mm-dd,
  // with no time component. In order to do so, we have to enhance
  // such date values with the correct time component so that the
  // DatePicker component will work properly.
  const date = isoDateWithLocalTimeAndZone(field.value);

  return <DatePicker
    {...field}
    {...props}
    value={date}
    onChange={val => {
      // Internally, the DatePicker component stores the selected date
      // as a JS Date object, with a time component that it, sadly, does not
      // ignore🤦 This means that in order for the datepicker to work properly
      // the react state representing the selected date must always have a time
      // component with the correct offset (matching the timezone offset of the
      // computer running the DatePicker component), as well as a non-zero time
      // component because the DatePicker doesn't work properly if the time
      // component is zeroed out, even though it's conceptually meaningless🤦🤦
      // https://github.com/mui-org/material-ui-pickers/issues/2024
      const res = formatISODate({representation: 'complete'}, val as Date);
      setFieldValue(field.name, res);
    }}
  />
};
export default FormikDatePicker;
